import React, { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { ArrowLeftRight, Braces, Copy, Search } from "lucide-react";
import Modal from "../../components/Modal";
import { optionsService } from "../../services/optionsService";
import {
  desenvolvedorService,
  type PedidoControleAcao,
  type PedidoControleAcaoDisponivel,
  type PedidoControleBuscaItem,
  type PedidoControleCanal,
  type PedidoControleDetalhe,
  type PedidoControleItem,
  type PedidoControleItemTipo,
} from "../../services/desenvolvedorService";
import { COLORS } from "../../constants";

type ConfirmState = {
  item: PedidoControleItem;
  acao: PedidoControleAcaoDisponivel;
} | null;

const STATUS_LABEL: Record<string, string> = {
  pending: "Pendente",
  in_progress: "Em andamento",
  done: "Concluído",
  cancelled: "Cancelado",
  cancelado: "Cancelado",
  concluido: "Concluído",
  finalizado: "Finalizado",
  aguardando_borda: "Aguardando borda",
  pendente_bordeiro: "Pendente bordeiro",
  no_forno: "No forno",
  processing: "Processando",
  confirmed: "Confirmado",
  ready: "Pronto",
  dispatched: "Despachado",
  delivered: "Entregue",
  concluded: "Concluído",
};

const ORDER_TYPE_LABEL: Record<string, string> = {
  delivery: "Entrega",
  takeout: "Retirada",
  indoor: "No local",
  salao: "Salão",
  "SALÃO": "Salão",
};

const ITEM_TIPO_LABEL: Record<PedidoControleItemTipo, string> = {
  fila_salgada: "Fila salgada",
  fila_doce: "Fila doce",
  fila_salao_salgada: "Fila salão salgada",
  fila_salao_doce: "Fila salão doce",
  forno: "Forno",
  fila_porcao: "Fila porção",
  fila_bebida: "Fila bebida",
  paozinho_preparo: "Pãozinho",
  fila_aguardando_entrega: "Aguardando entrega",
};

const labelStatus = (status: string | null | undefined): string => {
  if (!status) return "—";
  const key = status.trim().toLowerCase();
  return STATUS_LABEL[key] ?? STATUS_LABEL[status] ?? status;
};

const labelOrderType = (orderType: string | null | undefined): string => {
  if (!orderType) return "—";
  const key = orderType.trim().toLowerCase();
  return ORDER_TYPE_LABEL[orderType] ?? ORDER_TYPE_LABEL[key] ?? orderType;
};

const labelItemTipo = (tipo: PedidoControleItemTipo): string =>
  ITEM_TIPO_LABEL[tipo] ?? tipo;

const statusBadgeClass = (status: string): string => {
  const s = status.toLowerCase();
  if (s === "done" || s === "concluido" || s === "finalizado" || s === "concluded") {
    return "bg-emerald-100 text-emerald-800 border-emerald-200";
  }
  if (s === "cancelled" || s === "cancelado") {
    return "bg-neutral-200 text-neutral-700 border-neutral-300";
  }
  if (s === "aguardando_borda" || s === "pendente_bordeiro") {
    return "bg-amber-100 text-amber-900 border-amber-300";
  }
  if (s === "in_progress" || s === "no_forno" || s === "processing") {
    return "bg-blue-100 text-blue-800 border-blue-200";
  }
  return "bg-slate-100 text-slate-800 border-slate-200";
};

const acaoBtnClass = (acao: PedidoControleAcao): string => {
  if (acao === "avancar") return "bg-emerald-700 hover:bg-emerald-800 text-white";
  if (acao === "voltar") return "bg-amber-600 hover:bg-amber-700 text-white";
  if (acao === "cancelar") return "bg-red-700 hover:bg-red-800 text-white";
  return "bg-slate-700 hover:bg-slate-800 text-white";
};

const ControlePedidoDev: React.FC = () => {
  const [lojaId, setLojaId] = useState<number | "">("");
  const [displayId, setDisplayId] = useState("");
  const [orderId, setOrderId] = useState("");
  const [canal, setCanal] = useState<PedidoControleCanal | "">("");
  const [resultados, setResultados] = useState<PedidoControleBuscaItem[]>([]);
  const [detalhe, setDetalhe] = useState<PedidoControleDetalhe | null>(null);
  const [detalheAberto, setDetalheAberto] = useState(false);
  const [jsonAberto, setJsonAberto] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState>(null);

  const lojasQuery = useQuery({
    queryKey: ["lojas"],
    queryFn: () => optionsService.getLojas(),
  });

  const carregarDetalhe = useCallback(async (item: PedidoControleBuscaItem) => {
    const data = await desenvolvedorService.detalharPedido({
      canal: item.canal,
      pedidoIntegracaoId: item.pedidoIntegracaoId,
      pedidoFoodflyId: item.pedidoFoodflyId,
    });
    setDetalhe(data);
    setDetalheAberto(true);
  }, []);

  const buscar = useCallback(async () => {
    if (lojaId === "") {
      toast.error("Selecione a loja.");
      return;
    }
    const d =
      displayId.trim() !== "" ? parseInt(displayId.trim(), 10) : undefined;
    const o = orderId.trim() !== "" ? parseInt(orderId.trim(), 10) : undefined;
    setBuscando(true);
    setDetalhe(null);
    setDetalheAberto(false);
    setJsonAberto(false);
    try {
      const rows = await desenvolvedorService.buscarPedidos({
        lojaId,
        displayId: d != null && Number.isFinite(d) ? d : undefined,
        orderId: o != null && Number.isFinite(o) ? o : undefined,
        canal,
      });
      setResultados(rows);
      if (rows.length === 0) {
        toast.info("Nenhum pedido encontrado.");
      } else if (rows.length === 1) {
        await carregarDetalhe(rows[0]);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha na busca.");
    } finally {
      setBuscando(false);
    }
  }, [lojaId, displayId, orderId, canal, carregarDetalhe]);

  const recarregarDetalheAtual = useCallback(async () => {
    if (!detalhe) return;
    await carregarDetalhe({
      canal: detalhe.canal,
      pedidoIntegracaoId: detalhe.pedidoIntegracaoId,
      pedidoFoodflyId: detalhe.pedidoFoodflyId,
      lojaId: detalhe.lojaId,
      lojaNome: detalhe.lojaNome,
      displayId: detalhe.displayId,
      orderId: detalhe.orderId,
      customerName: detalhe.customerName,
      salaoIdentificador: detalhe.salaoIdentificador,
      statusIntegracao: detalhe.statusIntegracao,
      chegouEm: detalhe.chegouEm,
    });
  }, [detalhe, carregarDetalhe]);

  const moverMutation = useMutation({
    mutationFn: () => {
      if (!confirm) throw new Error("Sem confirmação");
      return desenvolvedorService.moverItem({
        tipo: confirm.item.tipo,
        id: confirm.item.id,
        acao: confirm.acao.acao,
        confirmacao: true,
      });
    },
    onSuccess: (result) => {
      toast.success(result.message || "Ação aplicada.");
      setConfirm(null);
      if (result.detalhe) {
        setDetalhe(result.detalhe);
        setDetalheAberto(true);
      } else {
        void recarregarDetalheAtual();
      }
    },
    onError: (e: Error) => {
      toast.error(e.message || "Falha ao mover item.");
    },
  });

  const payloadJson = useMemo(() => {
    if (!detalhe?.payloadOriginal) return null;
    try {
      return JSON.stringify(detalhe.payloadOriginal, null, 2);
    } catch {
      return String(detalhe.payloadOriginal);
    }
  }, [detalhe]);

  const copiarJson = useCallback(async () => {
    if (!payloadJson) {
      toast.info("Sem payload para copiar.");
      return;
    }
    try {
      await navigator.clipboard.writeText(payloadJson);
      toast.success("JSON copiado.");
    } catch {
      toast.error("Não foi possível copiar.");
    }
  }, [payloadJson]);

  const fecharDetalhe = () => {
    setDetalheAberto(false);
    setJsonAberto(false);
  };

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-950">
        <p className="font-semibold">Só perfil Desenvolvedor</p>
        <p className="mt-1">
          Controle total por item (avançar / voltar / cancelar / reabrir). Toda
          ação exige confirmação. Use com cuidado — altera produção em tempo
          real.
        </p>
      </section>

      <section className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm sm:p-6">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-neutral-900">
          <Search className="h-5 w-5" aria-hidden />
          Buscar pedido
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="text-sm font-medium text-neutral-700">
            Loja
            <select
              className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
              value={lojaId}
              onChange={(e) =>
                setLojaId(e.target.value === "" ? "" : Number(e.target.value))
              }
            >
              <option value="">Selecione</option>
              {(
                lojasQuery.data as { id: number; nome: string }[] | undefined
              )?.map((loja) => (
                <option key={loja.id} value={loja.id}>
                  {loja.nome}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-medium text-neutral-700">
            Nº do pedido (opcional)
            <input
              className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
              inputMode="numeric"
              value={displayId}
              onChange={(e) => setDisplayId(e.target.value)}
              placeholder="Vazio = todos do dia"
            />
          </label>
          <label className="text-sm font-medium text-neutral-700">
            ID externo (opcional)
            <input
              className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
              inputMode="numeric"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="Cardápio / FoodFly"
            />
          </label>
          <label className="text-sm font-medium text-neutral-700">
            Canal
            <select
              className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
              value={canal}
              onChange={(e) =>
                setCanal(e.target.value as PedidoControleCanal | "")
              }
            >
              <option value="">Todos</option>
              <option value="cardapio">Cardápio Web</option>
              <option value="salao">Salão (FoodFly)</option>
            </select>
          </label>
        </div>
        <button
          type="button"
          onClick={() => void buscar()}
          disabled={buscando}
          className="mt-4 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          style={{ backgroundColor: COLORS.PRIMARY }}
        >
          {buscando ? "Buscando…" : "Buscar"}
        </button>

        {resultados.length > 0 && (
          <ul className="mt-4 divide-y divide-neutral-100 rounded-md border border-neutral-200 text-sm">
            <li className="bg-neutral-50 px-3 py-2 text-xs font-semibold text-neutral-600">
              {resultados.length} pedido{resultados.length === 1 ? "" : "s"}
              {displayId.trim() === "" && orderId.trim() === ""
                ? " de hoje"
                : ""}
            </li>
            {resultados.map((r) => (
              <li key={`${r.canal}-${r.pedidoIntegracaoId ?? r.pedidoFoodflyId}`}>
                <button
                  type="button"
                  className={`flex w-full flex-wrap items-center justify-between gap-2 px-3 py-2 text-left hover:bg-neutral-50 ${
                    detalheAberto &&
                    detalhe &&
                    ((r.canal === "cardapio" &&
                      detalhe.pedidoIntegracaoId === r.pedidoIntegracaoId) ||
                      (r.canal === "salao" &&
                        detalhe.pedidoFoodflyId === r.pedidoFoodflyId))
                      ? "bg-red-50"
                      : ""
                  }`}
                  onClick={() => void carregarDetalhe(r)}
                >
                  <span>
                    <strong>#{r.displayId}</strong> ·{" "}
                    {r.canal === "salao" ? "Salão" : "Cardápio"} ·{" "}
                    {r.customerName || r.salaoIdentificador || "—"}
                  </span>
                  <span className="text-neutral-500">
                    {labelStatus(r.statusIntegracao)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Modal
        isOpen={detalheAberto && !!detalhe}
        onClose={fecharDetalhe}
        title={detalhe ? `Pedido #${detalhe.displayId}` : "Pedido"}
        panelClassName="max-w-4xl"
        bodyClassName="max-h-[75vh] overflow-y-auto p-6"
      >
        {detalhe && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span
                    className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-bold uppercase ${
                      detalhe.canal === "salao"
                        ? "border-red-700 bg-red-700 text-white"
                        : "border-slate-300 bg-slate-100 text-slate-800"
                    }`}
                  >
                    {detalhe.canal === "salao" ? "Salão" : "Cardápio"}
                  </span>
                  <span className="text-sm text-neutral-600">
                    {labelOrderType(detalhe.orderType)}
                  </span>
                </div>
                <p className="text-sm text-neutral-700">
                  {detalhe.lojaNome} · ID externo {detalhe.orderId}
                  {detalhe.customerName ? ` · ${detalhe.customerName}` : ""}
                  {detalhe.salaoMesaId ? ` · Mesa ${detalhe.salaoMesaId}` : ""}
                  {detalhe.salaoComandaId
                    ? ` · Comanda ${detalhe.salaoComandaId}`
                    : ""}
                </p>
                <p className="mt-1 text-xs text-neutral-500">
                  Integração: {labelStatus(detalhe.statusIntegracao)} · Status:{" "}
                  {labelStatus(detalhe.status)}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
                  onClick={() => setJsonAberto(true)}
                >
                  <Braces className="h-4 w-4" aria-hidden />
                  Ver JSON original
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
                  onClick={() => void recarregarDetalheAtual()}
                >
                  Atualizar
                </button>
              </div>
            </div>

            {detalhe.itens.length === 0 ? (
              <p className="text-sm text-neutral-600">
                Nenhum item de produção vinculado a este pedido.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {detalhe.itens.map((item) => (
                  <article
                    key={`${item.tipo}-${item.id}`}
                    className="rounded-lg border border-neutral-200 bg-neutral-50 p-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-bold text-neutral-900">
                          {item.label}
                        </p>
                        <p className="text-xs text-neutral-500">
                          {labelItemTipo(item.tipo)} #{item.id}
                        </p>
                      </div>
                      <span
                        className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase ${statusBadgeClass(
                          item.status,
                        )}`}
                      >
                        {labelStatus(item.status)}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {item.acoes.length === 0 ? (
                        <span className="text-xs text-neutral-500">
                          Sem ações neste status
                        </span>
                      ) : (
                        item.acoes.map((a) => (
                          <button
                            key={a.acao}
                            type="button"
                            title={a.descricao}
                            className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold ${acaoBtnClass(
                              a.acao,
                            )}`}
                            onClick={() => setConfirm({ item, acao: a })}
                          >
                            <ArrowLeftRight
                              className="h-3.5 w-3.5"
                              aria-hidden
                            />
                            {a.label}
                          </button>
                        ))
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={jsonAberto && !!detalhe}
        onClose={() => setJsonAberto(false)}
        title={`JSON original · Pedido #${detalhe?.displayId ?? ""}`}
        panelClassName="max-w-4xl"
        bodyClassName="max-h-[75vh] overflow-y-auto p-4 sm:p-6"
      >
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
            onClick={() => void copiarJson()}
          >
            <Copy className="h-4 w-4" aria-hidden />
            Copiar JSON
          </button>
        </div>
        {payloadJson ? (
          <pre className="overflow-x-auto rounded-lg border border-neutral-200 bg-neutral-950 p-4 text-xs leading-relaxed text-emerald-300">
            {payloadJson}
          </pre>
        ) : (
          <p className="text-sm text-neutral-600">
            Payload original do webhook não encontrado neste pedido.
          </p>
        )}
      </Modal>

      {confirm && (
        <div
          className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/45 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-mover-title"
        >
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h2
              id="confirm-mover-title"
              className="text-lg font-bold text-neutral-900"
            >
              Confirmar ação
            </h2>
            <p className="mt-2 text-sm text-neutral-700">
              <strong>{confirm.acao.label}</strong> em{" "}
              <strong>
                {confirm.item.label} ({labelItemTipo(confirm.item.tipo)} #
                {confirm.item.id})
              </strong>
              ?
            </p>
            <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
              {confirm.acao.descricao}
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium"
                disabled={moverMutation.isPending}
                onClick={() => setConfirm(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="rounded-lg bg-red-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                disabled={moverMutation.isPending}
                onClick={() => moverMutation.mutate()}
              >
                {moverMutation.isPending ? "Aplicando…" : "Sim, confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ControlePedidoDev;
